/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { jsx } from '@emotion/react'
import { Fragment } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  EuiButton,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useOrganizationIdp } from '@modules/security-idp-lib/hooks'

import { userAuthenticationUrl } from '@/apps/userconsole/urls'

import { noDataFlexGroupStyle, noDataLeftPanelStyle, noDataRightPanelStyle } from '../styles'
import UserAuthenticationSummary from '../UserAuthenticationSummary'
import CertificateExpiryWarning from '../CertificateExpiryWarning'

import { getLoginMethodMessage } from './messages'

import type { Props } from './types'

const UserAuthentication = ({
  organizationId,
  enforceAuthenticationMethod,
  permissions: { isLoading: isLoadingPermission, hasUpdateOrganizationIdp, hasViewOrganizationIdp },
  readonly,
}: Props) => {
  const navigate = useNavigate()
  const { isLoading: isLoadingIdp, data: idpInfo } = useOrganizationIdp(organizationId)

  const publicCertificate = idpInfo?.configuration.saml_idp.public_certificate[0]
  const isLoading = isLoadingPermission || isLoadingIdp

  if (!isLoading && !hasViewOrganizationIdp) {
    return null
  }

  return (
    <EuiPanel paddingSize='xl'>
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiTitle size='m'>
            <h2>
              <FormattedMessage
                id='organization.security.user-authentication.title'
                defaultMessage='User authentication'
              />
            </h2>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          {hasUpdateOrganizationIdp && !readonly && (
            <EuiButton
              size='m'
              isLoading={isLoading}
              onClick={() => navigate(userAuthenticationUrl())}
            >
              <FormattedMessage
                id='user-authentication.configure-sso-button'
                defaultMessage='Configure SSO'
              />
            </EuiButton>
          )}
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='l' />
      {isLoading ? (
        <Loading />
      ) : (
        <Fragment>
          {publicCertificate && (
            <CertificateExpiryWarning publicCertificate={publicCertificate} readonly={readonly} />
          )}
          <UserAuthenticationInfo
            isEnabledSamlSso={idpInfo?.configuration.enabled}
            isEnforcedSamlSso={enforceAuthenticationMethod === 'sso'}
            idpInfo={idpInfo}
          />
        </Fragment>
      )}
    </EuiPanel>
  )
}

const UserAuthenticationInfo = ({ isEnabledSamlSso, isEnforcedSamlSso, idpInfo }) => {
  const { formatMessage } = useIntl()
  const loginMethodMessage = formatMessage(
    getLoginMethodMessage(isEnabledSamlSso, isEnforcedSamlSso),
  )

  return (
    <Fragment>
      <EuiFlexGroup css={noDataFlexGroupStyle}>
        <EuiFlexItem css={noDataLeftPanelStyle}>
          <EuiTitle size='xs'>
            <h3>
              <FormattedMessage id='user-authentication.login-identifier' defaultMessage='Login' />
            </h3>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem css={noDataRightPanelStyle}>
          <EuiText>{loginMethodMessage}</EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='l' />
      {isEnabledSamlSso && <UserAuthenticationSummary idpInfo={idpInfo} />}
    </Fragment>
  )
}

const Loading = () => (
  <EuiEmptyPrompt
    icon={<EuiLoadingSpinner size='l' />}
    title={
      <FormattedMessage
        id='organization.security.user-authentication.loading'
        defaultMessage='Loading user authentication information'
      />
    }
    color='subdued'
  />
)

export default UserAuthentication
